import React, { useState } from 'react';
import './WebsiteDevelopment.css';
import EmailApi from '../../components/common/index'; 

const WebsiteDevelopment = () => {
  const [showForm, setShowForm] = useState(false);
  const [formStatus, setFormStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('');

    // Get form values
    const formData = new FormData(e.target);
    const name = formData.get('name')?.trim();
    const email = formData.get('email')?.trim();
    const phone = formData.get('phone')?.trim();
    const message = formData.get('message')?.trim();

    // ── VALIDATION ────────────────────────────────────────────────
    if (!name) {
      setFormStatus('Please enter your full name');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setFormStatus('Please enter a valid email address');
      return;
    }

    // Phone validation - exactly 10 digits, only numbers
    const phoneRegex = /^\d{10}$/;
    if (!phone || !phoneRegex.test(phone)) {
      setFormStatus('Please enter a valid 10-digit mobile number (only numbers)');
      return;
    }

    setFormStatus('Sending...');

    // Prepare data for backend
    const params = new URLSearchParams();
    params.append('full_name', name);
    params.append('email', email);
    params.append('phone', phone);
    params.append('subject', 'Website Development - Free Consultation Request');
    params.append('message', message || 'No additional details provided.');

    try {
      const { url, method } = EmailApi.SendMail;
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params,
      });

      if (response.ok) {
        setFormStatus('Thank you! We will get back to you soon 🚀');
        e.target.reset();
        setTimeout(() => {
          setFormStatus('');
          setShowForm(false);
        }, 4000);
      } else {
        throw new Error(`Server responded with status: ${response.status}`);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus('Failed to send message. Please try again later.');
      setTimeout(() => setFormStatus(''), 4000);
    }
  };

  const handlePhoneInput = (e) => {
    // Allow only digits
    e.target.value = e.target.value.replace(/[^0-9]/g, '');
  };

  return (
    <>
      <section className="wd-hero">
        <div className="wd-hero-content">
          <h1>Website Development</h1>
          <p>Unlock Your Online Potential with Bespoke Website Development</p>
        </div>
      </section>

      <section className="wd-purpose">
        <div className="wd-container">
          <h2><i className="fas fa-bullseye"></i> Our Purpose</h2>
          <p>
            At Oakya IT Services, we craft visually arresting, high-performance websites that serve as the digital heartbeat of your brand. Our expert team collaborates closely with you to understand your distinct goals and aspirations, delivering tailor-made solutions designed to accelerate your business growth.
          </p>
        </div>
      </section>

      <section className="wd-importance">
        <div className="wd-container">
          <h2><i className="fas fa-globe"></i> Why Website Development Matters</h2>
          <div className="wd-cards">
            <div className="wd-card">
              <i className="fas fa-mobile-alt"></i>
              <h3>Adaptive Responsive Design</h3>
              <p>Seamless experience across all devices.</p>
            </div>
            <div className="wd-card">
              <i className="fas fa-compass"></i>
              <h3>Intuitive Navigation</h3>
              <p>Effortlessly guides users to what matters.</p>
            </div>
            <div className="wd-card">
              <i className="fas fa-shield-alt"></i>
              <h3>Robust Security Protocols</h3>
              <p>Safeguarding your site and your visitors.</p>
            </div>
            <div className="wd-card">
              <i className="fas fa-search"></i>
              <h3>Built-in SEO Fundamentals</h3>
              <p>Laying the groundwork for visibility and reach.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="wd-blueprint" id="blueprint">
        <div className="wd-container">
          <h2><i className="fas fa-project-diagram"></i> Our Development Blueprint</h2>
          <div className="wd-timeline">
            <div className="wd-stage">
              <div className="wd-circle"><i className="fas fa-lightbulb"></i></div>
              <div className="wd-stage-content">
                <h3>Strategy & Blueprint</h3>
                <p>We begin with workshops to define objectives, target users, and content architecture.</p>
              </div>
            </div>
            <div className="wd-stage">
              <div className="wd-circle"><i className="fas fa-pencil-ruler"></i></div>
              <div className="wd-stage-content">
                <h3>Creative & Prototype</h3>
                <p>Designers sketch wireframes and stylistic direction for your approval.</p>
              </div>
            </div>
            <div className="wd-stage">
              <div className="wd-circle"><i className="fas fa-code"></i></div>
              <div className="wd-stage-content">
                <h3>Build & Review</h3>
                <p>Developers translate designs into code, then we test thoroughly for fluid usability.</p>
              </div>
            </div>
            <div className="wd-stage">
              <div className="wd-circle"><i className="fas fa-rocket"></i></div>
              <div className="wd-stage-content">
                <h3>Launch & Sustain</h3>
                <p>We deploy your site and provide post-launch support and performance tracking.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="wd-cta">
        <div className="wd-container">
          <h2><i className="fas fa-handshake"></i> Ready to Get Started?</h2>
          <h3>Launching a website doesn’t have to be daunting. Let’s simplify it.</h3>
          <ul>
            <li><strong>Free Initial Consult :</strong> We’ll discuss your vision, scope, and timelines.</li>
            <li><strong>Customized Proposal :</strong> Clear scope, estimated timeline, and a fair quote.</li>
            <li><strong>Ongoing Support :</strong> From deployment to maintenance, we've got your back.</li>
          </ul>
          <a className="wd-cta-btn" onClick={() => setShowForm(true)}>Book Your Free Consult</a>
        </div>
      </section>

      {showForm && (
        <div className="wd-popup-form active" onClick={() => setShowForm(false)}>
          <div className="wd-form-content" onClick={(e) => e.stopPropagation()}>
            <span className="wd-close-btn" onClick={() => setShowForm(false)}>×</span>
            <h2>Book Your Free Consultation</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input type="text" id="name" name="name" placeholder="Your Name" required />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  placeholder="example@gmail.com" 
                  required 
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Mobile Number</label>
                <input 
                  type="tel" 
                  id="phone" 
                  name="phone" 
                  placeholder="Mobile Number" 
                  maxLength={10}
                  required 
                  onInput={handlePhoneInput}
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Project Details</label>
                <textarea 
                  id="message" 
                  name="message" 
                  rows="4" 
                  placeholder="Tell us about your project, goals, timeline..." 
                />
              </div>

              <button type="submit" className="wd-submit-btn">Submit Request</button>
              
              {formStatus && (
                <p className={`wd-form-status ${formStatus.includes('Thank') ? 'success' : 'error'}`}>
                  {formStatus}
                </p>
              )}
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default WebsiteDevelopment;